import type { MaskRuleConfig } from "../contracts/types";
import type { FieldProfile } from "./profile";

export interface FieldCoverage {
  field: string;
  piiCandidate: boolean;
  strategy: MaskRuleConfig["strategy"];
  covered: boolean;
}

export interface CoverageSummary {
  perField: FieldCoverage[];
  overall: { relevant: number; covered: number; percentage: number };
  uncoveredFields: string[];
  /**
   * true cuando quedan ≥2 campos candidatos a cuasi-identificador sin
   * enmascarar simultáneamente (p. ej. código postal + fecha de nacimiento +
   * género), lo que puede permitir reidentificar a una persona combinando
   * columnas aunque ninguna por separado sea sensible. Mitiga directamente el
   * riesgo "falsa sensación de anonimato" (08-seguridad-privacidad.md).
   */
  combinationRisk: boolean;
  combinationRiskFields: string[];
}

const QUASI_IDENTIFIER_CATEGORIES = new Set(["date", "address", "identifier"]);

/**
 * Mide la cobertura de enmascarado (P13-R4): de los campos que el motor
 * marcó como candidatos a dato personal, cuántos tienen una regla efectiva
 * distinta de "preserve". Genera evidencia exportable como informe.
 */
export function computeCoverage(fieldProfiles: FieldProfile[], effectiveRules: MaskRuleConfig[]): CoverageSummary {
  const ruleByField = new Map(effectiveRules.map((rule) => [rule.field, rule]));
  const perField: FieldCoverage[] = [];
  const uncoveredFields: string[] = [];
  let relevant = 0;
  let covered = 0;

  for (const profile of fieldProfiles) {
    const rule = ruleByField.get(profile.name);
    const strategy = rule?.strategy ?? "preserve";
    const isCovered = strategy !== "preserve";
    if (profile.piiCandidate) {
      relevant += 1;
      if (isCovered) covered += 1;
      else uncoveredFields.push(profile.name);
    }
    perField.push({ field: profile.name, piiCandidate: profile.piiCandidate, strategy, covered: isCovered });
  }

  const combinationRiskFields = fieldProfiles
    .filter((profile) => QUASI_IDENTIFIER_CATEGORIES.has(profile.category))
    .filter((profile) => (ruleByField.get(profile.name)?.strategy ?? "preserve") === "preserve")
    .map((profile) => profile.name);

  return {
    perField,
    overall: { relevant, covered, percentage: relevant === 0 ? 100 : Math.round((covered / relevant) * 100) },
    uncoveredFields,
    combinationRisk: combinationRiskFields.length >= 2,
    combinationRiskFields,
  };
}
